/**
 * Compares pasted CSS with the house style: colors that are almost (or
 * exactly) a house color, fonts other than the house fonts, and corner radii
 * off the button and card scale. Each finding has a one-click fix that swaps in
 * the house token, keeping the value as the fallback so AI-tool previews and
 * pages without the tokens still look right.
 *
 * Fixes are text replacements, so the author's formatting and comments stay.
 */
import type { Finding } from './normalizeWidgetCode';
import type { HouseStyle } from './houseStyle';

export type BrandFixKind = 'colors' | 'fonts' | 'radii';

type Rgb = [number, number, number];

const parseHex = (hex: string): Rgb | null => {
  const h = hex.replace('#', '');
  if (h.length === 3) return [0, 1, 2].map(i => parseInt(h[i] + h[i], 16)) as Rgb;
  if (h.length === 6) return [0, 2, 4].map(i => parseInt(h.slice(i, i + 2), 16)) as Rgb;
  return null;
};

const distance = (a: Rgb, b: Rgb) => Math.sqrt(a.reduce((sum, v, i) => sum + (v - b[i]) ** 2, 0));
// Below this, two colors look the same side by side (e.g. #1e1e1e vs #1d1d1f).
const NEAR = 14;

// Fallback values inside var(--gx-…, …) are already tied to a token.
const stripGxVars = (css: string) => css.replace(/var\(\s*--gx-[\w-]+\s*,[^()]*(\([^()]*\)[^()]*)*\)/g, ' ');

const colorLiterals = (css: string): string[] => {
  const found = stripGxVars(css).match(/#[0-9a-fA-F]{6}\b|#[0-9a-fA-F]{3}\b|rgba?\(\s*\d+\s*,\s*\d+\s*,\s*\d+\s*(,\s*[\d.]+\s*)?\)/g) || [];
  return Array.from(new Set(found.map(c => c.toLowerCase())));
};

const toRgb = (literal: string): { rgb: Rgb; opaque: boolean } | null => {
  if (literal.startsWith('#')) {
    const rgb = parseHex(literal);
    return rgb ? { rgb, opaque: true } : null;
  }
  const n = literal.match(/[\d.]+/g) || [];
  const alpha = n.length === 4 ? Number(n[3]) : 1;
  return { rgb: [Number(n[0]), Number(n[1]), Number(n[2])], opaque: alpha >= 1 };
};

export type ColorMatch = { literal: string; token: string; value: string; exact: boolean };

/** Colors that are a house color, or close enough to be one. Translucent colors are left alone. */
export const matchColors = (css: string, style: HouseStyle): { matches: ColorMatch[]; offPalette: string[] } => {
  const palette = style.colors.map(c => ({ ...c, rgb: parseHex(c.value) })).filter(c => c.rgb) as Array<HouseStyle['colors'][number] & { rgb: Rgb }>;
  const matches: ColorMatch[] = [];
  const offPalette: string[] = [];
  colorLiterals(css).forEach((literal) => {
    const parsed = toRgb(literal);
    if (!parsed || !parsed.opaque || !palette.length) return;
    const best = palette.reduce((a, b) => (distance(parsed.rgb, a.rgb) <= distance(parsed.rgb, b.rgb) ? a : b));
    const d = distance(parsed.rgb, best.rgb);
    if (d <= NEAR) matches.push({ literal, token: best.token, value: best.value, exact: d === 0 });
    else offPalette.push(literal);
  });
  return { matches, offPalette };
};

// The first font in a stack decides the look; system stacks (-apple-system, Arial…) aren't the house fonts.
const HOUSE_FONTS = /^(roboto|noto sans sc|source han sans( sc)?|pingfang( sc)?|inherit|initial|unset)$/i;
const firstFamily = (value: string) => value.split(',')[0].trim().replace(/^['"]|['"]$/g, '');

/** font-family values whose first font isn't a house font. */
export const otherFonts = (css: string): string[] => {
  const out: string[] = [];
  const re = /font-family\s*:\s*([^;}]+)/gi;
  let m: RegExpExecArray | null;
  while ((m = re.exec(stripGxVars(css)))) {
    const value = m[1].trim();
    if (!value || /^var\(/i.test(value)) continue; // eslint-disable-line no-continue
    if (/mono/i.test(value)) continue; // eslint-disable-line no-continue -- code blocks may use a monospace font
    const first = firstFamily(value);
    if (!HOUSE_FONTS.test(first) && !out.includes(first)) out.push(first);
  }
  return out;
};

const pxValue = (v: string): number | null => {
  const m = v.trim().match(/^(\d+(?:\.\d+)?)(px|rem)$/);
  if (!m) return null;
  return m[2] === 'rem' ? Number(m[1]) * 16 : Number(m[1]);
};

/** border-radius values that look like pills or cards but aren't on the house scale. */
export const offScaleRadii = (css: string, style: HouseStyle): string[] => {
  const card = pxValue(style.radiusCard);
  const out: string[] = [];
  const re = /border-radius\s*:\s*([^;}]+)/gi;
  let m: RegExpExecArray | null;
  while ((m = re.exec(stripGxVars(css)))) {
    const value = m[1].trim();
    if (/var\(|%|\s/.test(value)) continue; // eslint-disable-line no-continue
    const px = pxValue(value);
    if (px === null) continue; // eslint-disable-line no-continue
    const pill = px >= 100 && value !== style.radiusButton;
    const offCard = px >= 14 && px <= 32 && card !== null && px !== card;
    if ((pill || offCard) && !out.includes(value)) out.push(value);
  }
  return out;
};

export const brandFindings = (css: string, style: HouseStyle): Finding[] => {
  const findings: Finding[] = [];
  const { matches, offPalette } = matchColors(css, style);
  const near = matches.filter(m => !m.exact);
  if (matches.length) {
    findings.push({
      id: 'brand-colors',
      level: near.length ? 'warn' : 'info',
      title: near.length
        ? `${near.length} ${near.length === 1 ? 'color is' : 'colors are'} almost a house color`
        : `${matches.length} house ${matches.length === 1 ? 'color is' : 'colors are'} written as plain values`,
      detail: 'Using the house tokens keeps this section in step when the house style changes.',
      items: matches.map(m => `${m.literal} → --gx-${m.token} (${m.value})`),
      ask: 'Write colors with the house tokens, e.g. var(--gx-ink, #1d1d1f).'
    });
  }
  if (offPalette.length) {
    findings.push({
      id: 'brand-off-palette',
      level: 'info',
      title: `${offPalette.length} ${offPalette.length === 1 ? 'color isn’t' : 'colors aren’t'} in the house style`,
      detail: 'Fine if intended (for example, product colors); otherwise ask for house colors.',
      items: offPalette,
      ask: 'Use only the house colors unless I ask for others.'
    });
  }
  const fonts = otherFonts(css);
  if (fonts.length) {
    findings.push({
      id: 'brand-fonts',
      level: 'warn',
      title: `Uses ${fonts.join(', ')} instead of the house fonts`,
      ask: 'Use the house fonts: var(--gx-font, \'Roboto\', sans-serif), or var(--gx-font-zh, \'Noto Sans SC\', sans-serif) for Chinese.'
    });
  }
  const radii = offScaleRadii(css, style);
  if (radii.length) {
    findings.push({
      id: 'brand-radii',
      level: 'info',
      title: `Corner radii off the house scale: ${radii.join(', ')}`,
      detail: `Buttons use ${style.radiusButton} (pills) and cards ${style.radiusCard}.`,
      ask: `Use var(--gx-radius-button, ${style.radiusButton}) for buttons and var(--gx-radius-card, ${style.radiusCard}) for cards.`
    });
  }
  return findings;
};

const escapeRe = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/** Applies the brand fixes to CSS text. */
export const applyBrandFixes = (css: string, style: HouseStyle, kinds: BrandFixKind[] = ['colors', 'fonts', 'radii']): string => {
  let out = css;
  // Keep existing var(--gx-…) calls out of the way while replacing.
  const held: string[] = [];
  const hold = (text: string) => text.replace(/var\(\s*--gx-[\w-]+\s*,[^()]*(\([^()]*\)[^()]*)*\)/g, (m) => { held.push(m); return `\uE000${held.length - 1}\uE000`; });
  const release = (text: string) => text.replace(/\uE000(\d+)\uE000/g, (_m, i) => held[Number(i)]);
  out = hold(out);
  if (kinds.includes('colors')) {
    matchColors(css, style).matches.forEach((m) => {
      let re: RegExp;
      if (m.literal.startsWith('#')) {
        re = new RegExp(`${escapeRe(m.literal)}(?![0-9a-fA-F])`, 'gi');
      } else {
        const [r, g, b] = (m.literal.match(/\d+(\.\d+)?/g) || []).slice(0, 3);
        re = new RegExp(`rgba?\\(\\s*${r}\\s*,\\s*${g}\\s*,\\s*${b}\\s*(,\\s*1(\\.0+)?\\s*)?\\)`, 'gi');
      }
      out = out.replace(re, `var(--gx-${m.token}, ${m.value})`);
    });
  }
  if (kinds.includes('fonts')) {
    const others = otherFonts(css);
    out = out.replace(/(font-family\s*:\s*)([^;}]*[^;}\s])(\s*)/gi, (whole, lead, value, trail) => {
      if (/^\s*var\(/i.test(value) || !others.includes(firstFamily(value))) return whole;
      const zh = /noto sans sc|pingfang|source han|microsoft yahei|hei/i.test(value);
      return `${lead}${zh ? `var(--gx-font-zh, ${style.fontZh})` : `var(--gx-font, ${style.fontEn})`}${trail}`;
    });
  }
  if (kinds.includes('radii')) {
    const radii = offScaleRadii(css, style);
    out = out.replace(/(border-radius\s*:\s*)([^;}]*[^;}\s])(\s*)/gi, (whole, lead, value, trail) => {
      const v = value.trim();
      if (!radii.includes(v)) return whole;
      const px = pxValue(v) || 0;
      return `${lead}${px >= 100 ? `var(--gx-radius-button, ${style.radiusButton})` : `var(--gx-radius-card, ${style.radiusCard})`}${trail}`;
    });
  }
  return release(out);
};
